import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Mail, XCircle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { api } from '@/lib/api';
import { replaceUrlSilently } from '@/lib/replace-url-silently';

// Lien de l'email de création de compte (tenant.signup.verify) : /verify?token=<magic link purpose 'verify'>
export const Route = createFileRoute('/verify')({
  validateSearch: (search: Record<string, unknown>): { token?: string } =>
    typeof search['token'] === 'string' && search['token'] ? { token: search['token'] } : {},
  component: VerifyRoute,
});

function VerifyRoute() {
  const { token } = Route.useSearch();
  return <VerifyEmailPage token={token} />;
}

// TD-011 (8.5.2) — après un succès, un rechargement arrive sans ?token (retiré de l'URL) : ce drapeau de session
// distingue « déjà vérifié » de « ouvert sans lien ». Effacé en quittant vers /login ou au bout d'une heure.
export const VERIFY_ATTEMPTED_KEY = 'verify-attempted';
const VERIFY_ATTEMPTED_AT_KEY = 'verify-attempted-at';
const VERIFY_ATTEMPTED_TTL_MS = 60 * 60 * 1000;

const clearVerifyAttempted = (): void => {
  try {
    sessionStorage.removeItem(VERIFY_ATTEMPTED_KEY);
    sessionStorage.removeItem(VERIFY_ATTEMPTED_AT_KEY);
  } catch { /* stockage indisponible : rien à effacer */ }
};

const readVerifyAttempted = (): boolean => {
  try {
    if (sessionStorage.getItem(VERIFY_ATTEMPTED_KEY) !== 'true') return false;
    const at = Number(sessionStorage.getItem(VERIFY_ATTEMPTED_AT_KEY));
    if (Number.isFinite(at) && Date.now() - at < VERIFY_ATTEMPTED_TTL_MS) return true;
    clearVerifyAttempted();
    return false;
  } catch {
    return false;
  }
};

const markVerifyAttempted = (): void => {
  try {
    sessionStorage.setItem(VERIFY_ATTEMPTED_KEY, 'true');
    sessionStorage.setItem(VERIFY_ATTEMPTED_AT_KEY, String(Date.now()));
  } catch { /* stockage indisponible : un rechargement affichera le message « sans lien » */ }
};

export function VerifyEmailPage({ token }: { token?: string }) {
  const nav = useNavigate();
  // Lu une fois au montage : seul un rechargement sans token s'en sert
  const [alreadyVerified] = useState(() => !token && readVerifyAttempted());

  // Le token (gardé en mémoire) sort de la barre d'adresse : ni historique, ni Referer, ni capture d'écran.
  // Routeur non notifié : sinon il relit /verify sans ?token et le POST ne part jamais.
  useEffect(() => {
    replaceUrlSilently('/verify');
  }, []);

  // Lien à usage unique : jamais de refetch (un second POST répondrait « déjà utilisé » après un succès).
  // L'échec est un état de la page, pas une erreur : pas de toast global « Erreur de chargement ».
  const verification = useQuery({
    queryKey: ['public-verify', token],
    queryFn: async (): Promise<'ok' | 'invalid'> => {
      try {
        await api.post('/public/verify', { token });
        return 'ok';
      } catch {
        return 'invalid';
      }
    },
    enabled: !!token,
    retry: false,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    if (verification.data === 'ok') markVerifyAttempted();
  }, [verification.data]);

  const noLink = !token && !alreadyVerified;
  const failed = !!token && verification.data === 'invalid';
  const verified = verification.data === 'ok' || alreadyVerified;
  const title = noLink
    ? 'Lien de vérification requis'
    : failed
      ? 'Lien expiré ou déjà utilisé'
      : alreadyVerified
        ? 'Compte déjà vérifié'
        : verified ? 'Adresse email vérifiée' : 'Vérification en cours…';
  const goToLogin = (): void => {
    clearVerifyAttempted();
    void nav({ to: '/login' });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-primary-900 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl text-primary-500">LogisTech-LGT</CardTitle>
          <p className="text-sm text-muted-foreground">Activation du compte</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start gap-2">
            {noLink && <Mail className="h-5 w-5 shrink-0 text-primary-500" />}
            {failed && <XCircle className="h-5 w-5 shrink-0 text-red-600" />}
            {verified && <CheckCircle2 className="h-5 w-5 shrink-0 text-green-600" />}
            <div>
              <p className="font-medium">{title}</p>
              <p className="text-sm text-muted-foreground">
                {noLink
                  ? 'Pour vérifier votre compte, cliquez sur le lien reçu par email. Si vous n’avez pas reçu l’email, vérifiez vos spams ou contactez-nous.'
                  : failed
                    ? 'Ce lien d’activation n’est plus valide. Si votre compte est déjà activé, connectez-vous.'
                    : alreadyVerified
                      ? 'Votre compte est déjà vérifié, vous pouvez vous connecter.'
                      : verified
                        ? 'Votre compte est activé, vous pouvez vous connecter.'
                        : 'Merci de patienter quelques instants.'}
              </p>
            </div>
          </div>
          {(noLink || failed || verified) && (
            <Button className="w-full" variant={verified ? 'default' : 'outline'} onClick={goToLogin}>
              Aller à la connexion
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
